import React, { Component } from "react";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { Field } from "formik";
import * as _ from "lodash";
import PropTypes from "prop-types";
import {
  confirmationNumberComponent,
  customInputComponent,
  CustomInputDateComponent,
  customInputSelectComponent,
} from "../../forms/customInputBronson";
import * as rk from "../../localization/resourceKeys";
import { verificationFormValidations } from "../../utils/validations";
import "./verificationForm.css";
import { SkipNavLink, SkipNavContent } from "@reach/skip-nav";
import "@reach/skip-nav/styles.css";
import BannerNotification from "../../banner/bannerNotification";
import {
  californiaLikeStates,
  statesInfo,
  utahLikeStates,
  virginiaLikeStates,
} from "../../utils/statesUS";

class VerificationForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      actualPin: props.initialValues.pinNumber || "",
      maskedPin: props.initialValues.pinNumber
        ? "*".repeat(props.initialValues.pinNumber.length)
        : "",
      actualSsn: props.initialValues.ssn || "",
      maskedSsn: props.initialValues.ssn
        ? "*".repeat(props.initialValues.ssn.length)
        : "",
    };

    this.handleMaskedSSN = this.handleMaskedSSN.bind(this);
    this.handleMaskedPin = this.handleMaskedPin.bind(this);
  }

  componentDidMount() {
    if (document.getElementById("ssn")) {
      document.getElementById("ssn").setAttribute("maxlength", 9);
    }
    if (document.getElementById("vin")) {
      document.getElementById("vin").setAttribute("maxlength", 17);
    }
    verificationFormValidations();
  }

  handleMaskedSSN(event, setFieldValue, valueName) {
    const actualSsn = this.state.actualSsn;
    const value = event.target.value;
    const length = value.length;
    const maxLength = valueName === "last4Ssn" ? 4 : 9;

    let newValue;
    if (actualSsn.length < length - 1 || actualSsn.length === length) {
      newValue = value.replace(/\D/g, "");
    } else {
      newValue =
        actualSsn.slice(0, length) + value.replace(/\D/g, "").slice(-1);
    }

    setFieldValue(valueName, newValue.slice(0, maxLength));
    this.setState({
      actualSsn: newValue.slice(0, maxLength),
      maskedSsn: "*".repeat(newValue.length).slice(0, maxLength),
    });
  }

  handleConfirmationNumber(event, setFieldValue, setIsVWGoAConfirmationNumber) {
    const confirmationNumber = event.target.value.toUpperCase();
    setFieldValue("confirmationNumber", confirmationNumber);
    const prefixes = ["VWGOA"];
    const isVWGoAPrefixDetected = prefixes.some((prefix) =>
      confirmationNumber.startsWith(prefix),
    );

    // Strict validation for VWGOA (for banner + submit)
    const VWGOA_REGEX = /^VWGOA_[A-Z0-9]{8}$/;
    const isVWGoAConfirmationNumber = VWGOA_REGEX.test(confirmationNumber);

    this.setState({
      confirmationNumber,
    });

    setIsVWGoAConfirmationNumber(
      isVWGoAConfirmationNumber,
      isVWGoAPrefixDetected,
    );

    if (this.props.hasSubmitted) {
      this.props.resetHasSubmitted();
    }
  }

  handleMaskedPin(event, setFieldValue) {
    const value = event.target.value.slice(0, 4);
    const length = value.length;
    const newValue = (
      this.state.actualPin.slice(0, length) + value[length - 1]
    ).slice(0, length);
    setFieldValue("pinNumber", newValue);
    this.setState({
      actualPin: newValue,
      maskedPin: new Array(length + 1).join("*"),
    });
  }

  render() {
    const {
      t,
      setFieldValue,
      isDNS,
      isDeleteMyInfo,
      isKnowMyInfo,
      country,
      requestType,
      doNotShowDoB,
      doNotShowSSN,
      values,
      showVWGoABanner,
    } = this.props;
    const currentStateResidence =
      this.props.stateSelector.currentStateResidence;
    const selectedRequest = this.props.selectedRequest;
    const areProtectionServices =
      this.props?.stateSelector?.brandAffiliation ===
      t(rk.BRANDS_OPTION_VWD_APP_DER);
    const isCosumerNotFoundError =
      this.props?.networkError?.error?.data?.experianResponseCodes
        ?.errorQkbMessage ===
      t(rk.EXPERIAN_FORM.API_CONSUMER_NOT_FOUND_RESPONSE) || false;
    const secureKeyBirthDate = this.props.secureKey?.birthDate;
    const isQuebec = country.toLowerCase() === "canada";

    const isTier2 = requestType === "TIER2_RPT";
    const isCorrectDataRequest =
      selectedRequest === t(rk.REQUEST_SELECTOR.CORRECT_MY_DATA);

    const statesToHidePinFor = [
      ...californiaLikeStates,
      ...virginiaLikeStates,
      ...utahLikeStates,
    ];

    const showPinField =
      isCorrectDataRequest ||
      (isKnowMyInfo && isTier2 && !isQuebec) ||
      (!statesToHidePinFor.includes(currentStateResidence) && !isQuebec);

    let dateOfBirthProps = {
      name: "dateOfBirth",
      dataTestId: "dateOfBirthField",
      placeHolder: t(rk.COMMON.DOB),
      altPlaceholder: t(rk.COMMON.DOB).toUpperCase(),
      type: "date",
      id: "dateOfBirth",
      component: CustomInputDateComponent,
      setFieldValue: setFieldValue,
      error: this.props.errors.dateOfBirth,
      helpContent:
        this.props.errors.dateOfBirth && this.props.touched.dateOfBirth
          ? this.props.errors.dateOfBirth
          : "",
    };

    if (isQuebec && secureKeyBirthDate !== undefined) {
      dateOfBirthProps.disabled = secureKeyBirthDate;
      dateOfBirthProps.secureKeyDoB = true;
    }

    const showReportSelectionDropdown = () => {
      if (!areProtectionServices) {
        if (isKnowMyInfo) return true;
      } else if (areProtectionServices) {
        if (this.props?.stateSelector?.currentStateResidence === "CA")
          return true;
      }
      return false;
    };

    const { networkError } = this.props;
    let errorMessage = t(rk.ERROR_API_FAIL);
    let vwGoARedirectMessage1 = t(rk.ERROR_ISVWGOA_MESS1);
    let vwGoARedirectMessage2 = t(rk.ERROR_ISVWGOA_MESS2);

    if (isCosumerNotFoundError) {
      errorMessage = t(rk.EXPERIAN_FORM.API_VERIFY_ERROR);
    } else if (networkError?.error === "Bad_UserRequestNotFound") {
      errorMessage = t(rk.ERROR_USER_NOT_FOUND);
    }

    return (
      <>
        <SkipNavLink />
        <div className="o-layout u-m-none">
          <div className="o-layout__item  u-p-none@xs u-1/1">
            <span className="note2">{t(rk.VERFICIATION_FORM.NOTE1)}</span>
          </div>
        </div>
        {!_.isEmpty(this.props?.networkError) && (
          <BannerNotification notificationStyle="error">
            {errorMessage}
          </BannerNotification>
        )}
        {showVWGoABanner && (
          <BannerNotification notificationStyle="info">
            <div>{vwGoARedirectMessage1}</div>

            <div className="banner-spacing">
              {vwGoARedirectMessage2}
              <a
                href={
                  process.env.REACT_APP_AWS_ENV === "PRODUCTION"
                    ? process.env.REACT_APP_PRIVACY
                    : process.env.REACT_APP_LEGACY_PRIVACY
                }
                target="_blank"
                rel="noopener noreferrer"
                className="banner-link"
              >
                VW Privacy Portal.
              </a>

            </div>
          </BannerNotification>
        )}
        <div className="o-layout u-m-none">
          <div className="o-layout__item u-mh-none u-p-none@xs verify-form-title">
            {t(rk.VERFICIATION_FORM.TITLE)}
          </div>
        </div>
        <div className="o-layout u-m-none">
          {showReportSelectionDropdown() && (
            <div className="o-layout__item u-1/2 u-1/1@xs u-1/1@s u-p-none@xs u-m-none">
              <Field
                name="reportType"
                id="reportType"
                dataTestId="reportType"
                placeHolder={t(rk.COMMON.REQUESTED_REPORT)}
                component={customInputSelectComponent}
                options={[
                  {
                    name: t(rk.INFO_FORM.REPORT_CATEGORY_CARD_HEADER),
                    abbreviation: t(rk.INFO_FORM.REPORT_CATEGORY_CARD_HEADER),
                  },
                  {
                    name: t(rk.INFO_FORM.REPORT_SPECIFIC_PIECES),
                    abbreviation: t(rk.INFO_FORM.REPORT_SPECIFIC_PIECES),
                  },
                ]}
                setFieldValue={setFieldValue}
                error={this.props.errors.reportType}
                helpContent={
                  this.props.errors.reportType && this.props.touched.reportType
                    ? this.props.errors.reportType
                    : ""
                }
              />
            </div>
          )}

          {areProtectionServices && (
            <div className="o-layout__item u-1/2 u-1/1@xs u-1/1@s u-p-none@xs u-m-none">
              <SkipNavContent />
              <Field
                name="firstName"
                placeHolder={t(rk.COMMON.FIRST_NAME)}
                altPlaceholder={t(rk.COMMON.FIRST_NAME).toUpperCase()}
                type="text"
                id="firstName"
                component={customInputComponent}
                error={this.props.errors.firstName}
                helpContent={
                  this.props.errors.firstName && this.props.touched.firstName
                    ? this.props.errors.firstName
                    : ""
                }
                dataTestId="firstNameField"
              />
            </div>
          )}

          <div
            className={`o-layout__item ${isKnowMyInfo || (areProtectionServices && !showReportSelectionDropdown()) ? "u-1/2 u-1/1@xs u-1/1@s" : "u-1/1"} u-p-none@xs u-m-none`}
          >
            <SkipNavContent />
            <Field
              name="lastName"
              placeHolder={t(rk.COMMON.LAST_NAME)}
              altPlaceholder={t(rk.COMMON.LAST_NAME).toUpperCase()}
              type="text"
              id="lastName"
              component={customInputComponent}
              secureKeyProps={this.props.secureKey}
              disabled={this.props.secureKey?.familyName}
              error={this.props.errors.lastName}
              helpContent={
                this.props.errors.lastName && this.props.touched.lastName
                  ? this.props.errors.lastName
                  : ""
              }
              dataTestId="lastNameField"
            />
          </div>
        </div>
        <div className="o-layout u-m-none">
          {!areProtectionServices && (
            <div
              className={`o-layout__item ${showPinField ? "u-1/2 u-1/1@xs u-1/1@s" : "u-1/1"} u-p-none@xs u-m-none`}
            >
              <Field
                name="confirmationNumber"
                dataTestId="confirmationNumberField"
                placeHolder={t(rk.COMMON.CONFIRMATION_NUM)}
                altPlaceholder={t(rk.COMMON.CONFIRMATION_NUM).toUpperCase()}
                type="text"
                id="confirmationNumber"
                component={confirmationNumberComponent}
                onChange={(e) =>
                  this.handleConfirmationNumber(
                    e,
                    setFieldValue,
                    this.props.setIsVWGoAConfirmationNumber,
                  )
                }
                error={this.props.errors.confirmationNumber}
                helpContent={
                  this.props.errors.confirmationNumber &&
                    this.props.touched.confirmationNumber
                    ? this.props.errors.confirmationNumber
                    : ""
                }
              />
            </div>
          )}
          {showPinField && !areProtectionServices && (
            <div
              className={`o-layout__item u-1/2 u-1/1@xs u-1/1@s u-p-none@xs u-m-none pin-wrapper`}
            >
              <Field
                name="pinNumber"
                data-testid="pinNumber"
                dataTestId="pinNumberField"
                placeHolder={t(rk.COMMON.PIN)}
                altPlaceholder={t(rk.COMMON.PIN).toUpperCase()}
                type="text"
                id="pinNumber"
                component={customInputComponent}
                error={this.props.errors.pinNumber}
                helpContent={
                  this.props.errors.pinNumber && this.props.touched.pinNumber
                    ? this.props.errors.pinNumber
                    : ""
                }
                onChange={(e) =>
                  this.handleMaskedPin(e, setFieldValue, "pinNumber")
                }
                value={this.state.maskedPin}
                hint={t(rk.VERFICIATION_FORM.PIN_HINT_MESSAGE)}
              />
            </div>
          )}
        </div>
        {!(isDNS || isDeleteMyInfo) &&
          (!doNotShowDoB || !doNotShowSSN) &&
          (values.confirmationNumber?.length ||
            areProtectionServices ||
            isQuebec) && (
            <div className="o-layout u-m-none">
              {!doNotShowDoB && (
                <div
                  className={`o-layout__item ${isQuebec ? "u-1/1" : "u-1/2 u-1/1@xs u-1/1@s"} u-p-none@xs u-m-none`}
                >
                  <Field {...dateOfBirthProps} />
                </div>
              )}
              {!isQuebec && !areProtectionServices && !doNotShowSSN && (
                <div className="o-layout__item u-1/2 u-1/1@xs u-1/1@s u-p-none@xs u-m-none">
                  <Field
                    name="last4Ssn"
                    data-testid="last4Ssn"
                    dataTestId="last4SsnField"
                    placeHolder={t(rk.COMMON.LAST4_SSN)}
                    altPlaceholder={t(rk.COMMON.LAST4_SSN).toUpperCase()}
                    type="text"
                    id="last4Ssn"
                    component={customInputComponent}
                    error={this.props.errors.last4Ssn}
                    helpContent={
                      this.props.errors.last4Ssn && this.props.touched.last4Ssn
                        ? this.props.errors.last4Ssn
                        : ""
                    }
                    onChange={(e) =>
                      this.handleMaskedSSN(e, setFieldValue, "last4Ssn")
                    }
                    value={this.state.maskedSsn}
                  />
                </div>
              )}
              {areProtectionServices && !doNotShowSSN && (
                <div className="o-layout__item u-1/2 u-1/1@xs u-1/1@s u-p-none@xs u-m-none">
                  <Field
                    name="ssn"
                    data-testid="ssn"
                    dataTestId="ssnField"
                    placeHolder={t(rk.COMMON.FULL_SSN)}
                    altPlaceholder={t(rk.COMMON.FULL_SSN).toUpperCase()}
                    type="text"
                    id="ssn"
                    component={customInputComponent}
                    error={this.props.errors.ssn}
                    helpContent={
                      this.props.errors.ssn && this.props.touched.ssn
                        ? this.props.errors.ssn
                        : ""
                    }
                    onChange={(e) =>
                      this.handleMaskedSSN(e, setFieldValue, "ssn")
                    }
                    value={this.state.maskedSsn}
                  />
                </div>
              )}
            </div>
          )}
        <div className="o-layout u-m-none">
          {areProtectionServices && (
            <div>
              <div
                className={`o-layout__item u-1/2 u-1/1@xs u-1/1@s u-p-none@xs u-m-none pin-wrapper`}
              >
                <Field
                  name="addressLine1"
                  data-testid="addressLine1"
                  type="text"
                  placeHolder={t(rk.COMMON.ADDRESS_LINE1)}
                  altPlaceholder={t(rk.COMMON.ADDRESS_LINE1).toUpperCase()}
                  id="addressLine1"
                  component={customInputComponent}
                  error={this.props.errors.addressLine1}
                  helpContent={
                    this.props.errors.addressLine1 &&
                      this.props.touched.addressLine1
                      ? this.props.errors.addressLine1
                      : ""
                  }
                />
              </div>
              <div
                className={`o-layout__item u-1/2 u-1/1@xs u-1/1@s u-p-none@xs u-m-none pin-wrapper`}
              >
                <Field
                  name="city"
                  data-testid="city"
                  type="text"
                  placeHolder={t(rk.COMMON.CITY)}
                  altPlaceholder={t(rk.COMMON.CITY).toUpperCase()}
                  id="city"
                  component={customInputComponent}
                  error={this.props.errors.city}
                  helpContent={
                    this.props.errors.city && this.props.touched.city
                      ? this.props.errors.city
                      : ""
                  }
                />
              </div>
              <div
                className={`o-layout__item u-1/2 u-1/1@xs u-1/1@s u-p-none@xs u-m-none pin-wrapper`}
              >
                <Field
                  name="state"
                  data-testid="state"
                  placeHolder={t(rk.COMMON.SELECT_STATE)}
                  id="state"
                  component={customInputSelectComponent}
                  options={statesInfo}
                  setFieldValue={setFieldValue}
                  error={this.props.errors.state}
                  helpContent={
                    this.props.errors.state && this.props.touched.state
                      ? this.props.errors.state
                      : ""
                  }
                />
              </div>
              <div
                className={`o-layout__item u-1/2 u-1/1@xs u-1/1@s u-p-none@xs u-m-none pin-wrapper`}
              >
                <Field
                  name="postalCode"
                  data-testid="postalCode"
                  type="text"
                  placeHolder={t(rk.COMMON.ZIP_CODE)}
                  altPlaceholder={t(rk.COMMON.ZIP_CODE).toUpperCase()}
                  id="postalCode"
                  component={customInputComponent}
                  error={this.props.errors.postalCode}
                  helpContent={
                    this.props.errors.postalCode &&
                      this.props.touched.postalCode
                      ? this.props.errors.postalCode
                      : ""
                  }
                  setFieldValue={setFieldValue}
                />
              </div>
              <div
                className={`o-layout__item u-1/1 u-1/1@xs u-1/1@s u-p-none@xs u-m-none pin-wrapper`}
              >
                <Field
                  name="vin"
                  data-testid="vin"
                  placeHolder={t(rk.COMMON.VIN)}
                  altPlaceholder={t(rk.COMMON.VIN).toUpperCase()}
                  type="text"
                  id="vin"
                  component={customInputComponent}
                  error={this.props.errors.vin}
                  helpContent={
                    this.props.errors.vin && this.props.touched.vin
                      ? this.props.errors.vin
                      : ""
                  }
                  value={values.vin?.toUpperCase()}
                />
              </div>
            </div>
          )}
        </div>
      </>
    );
  }
}

VerificationForm.propTypes = {
  t: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  isDNS: PropTypes.bool,
  isDeleteMyInfo: PropTypes.bool,
  isKnowMyInfo: PropTypes.bool,
  country: PropTypes.string,
  requestType: PropTypes.string,
  stateSelector: PropTypes.object,
  selectedRequest: PropTypes.string,
  errors: PropTypes.object,
  networkError: PropTypes.object,
  initialValues: PropTypes.object,
  touched: PropTypes.object,
  brandAffiliation: PropTypes.string,
  secureKey: PropTypes.object,
  isQuebec: PropTypes.bool,
  isCorrectDataRequest: PropTypes.bool,
  showPinField: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  country: state.initialReducer.country,
  stateSelector: state.stateSelector,
  selectedRequest: state.requestSelector.selectedRequest,
  secureKey: state.secureKey,
});

export default connect(mapStateToProps)(withTranslation()(VerificationForm));
